import {
 Component,
 computed,
 input,
 signal,
 effect,
 ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { secureRandomHex } from 'src/app/core/utilities';
@Component({
 selector: 'circle-progress',
 standalone: true,
 imports: [CommonModule],
 styleUrls: ['./circle-progress.component.scss'],
 changeDetection: ChangeDetectionStrategy.OnPush,
 templateUrl: './circle-progress.component.html'
})
export class CircleProgressComponent {
 percent                      = input<number>(0);
 radius                       = input<number>(50);
 space                        = input<number>(4);
 outerStrokeWidth             = input<number>(10);
 outerStrokeColor             = input<string>('#78C000');
 outerStrokeGradient          = input<boolean>(false);
 outerStrokeGradientStopColor = input<string>('#C7E596');
 innerStrokeWidth             = input<number>(10);
 innerStrokeColor             = input<string>('#e7e8ea');
 title                        = input<string>('auto');
 titleFontSize                = input<string>('17');
 titleFontWeight              = input<string>('600');
 titleColor                   = input<string>('#444444');
 showTitle                    = input<boolean>(true);
 showUnits                    = input<boolean>(true);
 units                        = input<string>('%');
 subtitle                     = input<string>('');
 subtitleFontSize             = input<string>('12');
 subtitleColor                = input<string>('#888');
 showSubtitle                 = input<boolean>(false);
 animation                    = input<boolean>(true);
 animationDuration            = input<number>(100); // ms
 readonly gradientId = `outer-linear-${secureRandomHex(4)}`;
 // ── Animation ─────────────────────────────────────────────────────────
 animatedPercent = signal(0);
 private _animFrame: number | null = null;
 constructor() {
   effect(() => {
     const target   = this.percent();
     const duration = this.animationDuration();
     if (!this.animation()) {
       this.animatedPercent.set(target);
       return;
     }
     // ยกเลิก animation เดิมก่อน
     if (this._animFrame !== null) {
       cancelAnimationFrame(this._animFrame);
     }
     const start    = this.animatedPercent();
     const diff     = target - start;
     const startTime = performance.now();
     const animate = (now: number) => {
       const elapsed  = now - startTime;
       const progress = Math.min(elapsed / duration, 1);
       // easeInOutQuad
       const ease = progress < 0.5
         ? 2 * progress * progress
         : 1 - Math.pow(-2 * progress + 2, 2) / 2;
       this.animatedPercent.set(start + diff * ease);
       if (progress < 1) {
         this._animFrame = requestAnimationFrame(animate);
       }
     };
     this._animFrame = requestAnimationFrame(animate);
   });
 }
 // ── Computed ──────────────────────────────────────────────────────────
 center  = computed(() => this.radius() + this.outerStrokeWidth());
 svgSize = computed(() => this.center() * 2);
 viewBox = computed(() => `0 0 ${this.svgSize()} ${this.svgSize()}`);
 arcPath = computed(() => {
   const pct        = Math.min(Math.max(this.animatedPercent(), 0), 100);
   const r          = this.radius();
   const cx         = this.center();
   const cy         = this.center();
   const startAngle = -Math.PI / 2;
   const endAngle   = startAngle + (2 * Math.PI * pct) / 100;
   const x1 = cx + r * Math.cos(startAngle);
   const y1 = cy + r * Math.sin(startAngle);
   const x2 = cx + r * Math.cos(endAngle);
   const y2 = cy + r * Math.sin(endAngle);
   const largeArcFlag = pct > 50 ? 1 : 0;
   if (pct >= 100) {
     const midX = cx + r * Math.cos(startAngle + Math.PI);
     const midY = cy + r * Math.sin(startAngle + Math.PI);
     return `M ${x1} ${y1} A ${r} ${r} 0 0 1 ${midX} ${midY} A ${r} ${r} 0 0 1 ${x1} ${y1}`;
   }
   return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArcFlag} 1 ${x2} ${y2}`;
 });
 titleText = computed(() => {
   if (this.title() !== 'auto') return this.title();
   return this.showUnits()
     ? `${this.percent()}${this.units()}`
     : `${this.percent()}`;
 });
 titleY = computed(() =>
   this.showSubtitle()
     ? this.center() - Number(this.subtitleFontSize()) / 2 - 2
     : this.center()
 );
 subtitleY = computed(() =>
   this.center() + Number(this.titleFontSize()) / 2 + 4
 );
}